// src/views/Users/Customers/Modal/AddProjectModal.jsx
import React, { useEffect, useState, useRef } from "react";
import { Modal, Button } from "react-bootstrap";
import { useForm, useFieldArray, useWatch, Controller } from "react-hook-form";
import { modeConstants, statusConstants } from "../../constants";
import { useDispatch, useSelector } from "react-redux";
import { projectActions } from "../../redux/actions";
import { ToastifyService } from "../../services";
import Select from "react-select";
import { getAllCurrencies } from "../utility";
import JoditEditorComponent from "../SummernoteEditor";

const DEFAULT_VALUES = {
  id: "",
  positionName: "",
  companyId: "",
  companyName: "",
  totalOpenPositions: "",
  jobType: "",
  contractDetails: "",
  frequency: "",
  status: "",
  location: "",
  salaryRange: "",
  gender: "",
  nationality: "",
  minimumExperienceRquired: "",
  currency: "",
  projectPositions: [
    { id: "", projectId: "", userId: "", assignedPositions: 1 },
  ],
  jobRequirements: "",
};

const genderOptions = ["Male", "Female", "Other"];
const salaryRangeOptions = [
  "0-10000",
  "10001-20000",
  "20001-30000",
  "30001-40000",
  "40001-50000",
  "50001-60000",
  "60001-70000",
  "70001-80000",
  "80001-90000",
  "90001-100000",
  "100000+",
];
export default function AddProjectModal({
  show,
  onHide,
  mode,
  isCompanyDataAvailable = true,
}) {
  const isAdd = mode === modeConstants.ADD_MODE;
  const isEdit = mode === modeConstants.UPDATE_MODE;
  const isView = mode === modeConstants.VIEW_MODE;

  const dispatch = useDispatch();
  const initialData = useSelector((s) => s.projectReducer.formData);
  const companyData = useSelector((s) => s.companyReducer.formData);
  const companyAllData = useSelector((s) => s.companyReducer.allData) || [];
  const recruiterAllData = useSelector((s) => s.recruiterReducer.allData) || [];
  const [jdFile, setJDFile] = useState(null);
  const [jdDragOver, setJDDragOver] = useState(false);
  const jdInputRef = useRef(null);

  const [preFile, setPreFile] = useState(null);
  const [preDragOver, setPreDragOver] = useState(false);
  const preInputRef = useRef(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: DEFAULT_VALUES, mode: "onChange",          // validate on typing
    reValidateMode: "onChange",
    criteriaMode: "all",
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "projectPositions",
  });

  // Watch dynamic rows to filter recruiter options per row
  const assignedValues =
    useWatch({
      control,
      name: "projectPositions",
    }) || [];

  // Return recruiters not used by other rows; always keep the current row's selection
  const filteredRecruitersFor = (rowIndex) => {
    const currentSelected = String(assignedValues?.[rowIndex]?.userId ?? "");
    const selectedSet = new Set(
      assignedValues
        .map((v) => (v?.userId != null ? String(v.userId) : ""))
        .filter((id) => id !== "")
    );

    return recruiterAllData.filter((r) => {
      const id = String(r.id);
      return id === currentSelected || !selectedSet.has(id);
    });
  };

  // Prefill on open based on mode + initialData
  useEffect(() => {
    if (!show) return;

    if (isAdd) {
      // If a row was provided, just prefill company name/id; keep the rest empty
      const companyName =
        initialData?.companyName ?? companyData?.companyName ?? "";
      const companyId =
        initialData?.companyId?.toString?.() ??
        companyData?.id?.toString?.() ??
        initialData?.id?.toString?.() ??
        "";
      setJDFile(null);
      setPreFile(null);
      reset({
        ...DEFAULT_VALUES,
        companyName,
        companyId,
      });
      replace(DEFAULT_VALUES.projectPositions);
      return;
    }

    if (initialData && (isEdit || isView)) {
      reset({
        positionName: initialData.positionName ?? "",
        companyId:
          initialData.companyId?.toString?.() ??
          initialData.id?.toString?.() ??
          companyData?.id?.toString?.() ??
          "",
        totalOpenPositions: initialData.totalOpenPositions ?? "",
        jobType: initialData.jobType ?? "",
        contractDetails: initialData.contractDetails ?? "",
        frequency: initialData.frequency ?? "",
        status: initialData.status ?? "",
        location: initialData.location ?? "",
        gender: initialData?.gender,
        nationality: initialData?.nationality,
        minimumExperienceRquired: initialData?.minimumExperienceRquired ?? "",
        salaryRange: initialData?.salaryRange ?? "",
        currency: initialData.currency ?? "",
        // Map assigned list if available, else keep one empty row
        projectPositions: (initialData.projectPositions || []).map((p) => ({
          id: p?.id?.toString?.() ?? "",
          projectId: p?.projectId?.toString?.() ?? "",
          userId: p?.userId?.toString?.() ?? "",
          assignedPositions:
            Number.isFinite(Number(p?.assignedPositions)) &&
              Number(p?.assignedPositions) > 0
              ? Number(p.assignedPositions)
              : 1,
        })) || [{ id: "", projectId: "", userId: "", assignedPositions: 1 }],
        jobRequirements: initialData.details ?? "",
      });

      replace(
        (initialData.projectPositions || []).map((p) => ({
          id: p?.id?.toString?.() ?? "",
          projectId: p?.projectId?.toString?.() ?? "",
          userId: p?.userId?.toString?.() ?? "",
          assignedPositions:
            Number.isFinite(Number(p?.assignedPositions)) &&
              Number(p?.assignedPositions) > 0
              ? Number(p.assignedPositions)
              : 1,
        })) || [{ id: "", projectId: "", userId: "", assignedPositions: 1 }]
      );
      setJDFile(null);
      setPreFile(null);
      return;
    }

    // Fallback
    reset(DEFAULT_VALUES);
    replace(DEFAULT_VALUES.projectPositions);
    setJDFile(null);
    setPreFile(null);
  }, [show, isAdd, isEdit, isView, initialData, companyData, reset, replace]);

  const onSubmit = async (values) => {
    const safeNumber = (v, fallback = 0) => {
      if (v === "" || v === null || v === undefined) return fallback;
      const n = Number(String(v).replace(/[^0-9.-]+/g, ""));
      return Number.isNaN(n) ? fallback : n;
    };

    const assigned = (values.projectPositions || [])
      .map((p) => ({
        userId: p?.userId ? p.userId : "",
        assignedPositions: safeNumber(p?.assignedPositions, 0),
      }))
      .filter((p) => p.userId && p.assignedPositions > 0);

    let comId = isCompanyDataAvailable ? companyData?.id : 0;
    let id = isAdd ? 0 : initialData.id;
    //-----------------------------------------
    // CREATE FormData
    //-----------------------------------------
    const fd = new FormData();

    //-----------------------------------------
    // Append normal fields to FormData
    //-----------------------------------------
    fd.append("id", id || 0);
    fd.append("positionName", values.positionName?.trim() ?? "");
    fd.append("companyId", values.companyId ?? "");
    fd.append("companyName", values.companyName?.trim() ?? "");
    fd.append("totalOpenPositions", safeNumber(values.totalOpenPositions, 0));
    fd.append("jobType", values.jobType ?? "");
    fd.append("contractDetails", values.contractDetails ?? "");
    fd.append("frequency", values.frequency ?? "");
    fd.append("status", values.status ?? "");
    fd.append("location", values.location?.trim() ?? "");
    fd.append("gender", values.gender?.trim() ?? "");
    fd.append(
      "minimumExperienceRquired",
      values.minimumExperienceRquired
    );
    fd.append("nationality", values.nationality?.trim() ?? "");
    fd.append("salaryRange", values.salaryRange?.trim() ?? "");
    fd.append("currency", values.currency ?? "");
    fd.append("details", values?.jobRequirements?.trim() ?? "");

    //-----------------------------------------
    // projectPositions must be JSON
    //-----------------------------------------
    fd.append("ProjectPositionsJson", JSON.stringify(assigned));

    //-----------------------------------------
    // Handle JD file
    //-----------------------------------------
    let jd = jdFile;
    if (jd && !(jd instanceof File) && jd?.[0]) jd = jd[0];

    if (jd instanceof File) {
      fd.append("JD", jd, jd.name);
    } else if (initialData?.jdUrl && isEdit) {
      fd.append("JDUrl", initialData.jdUrl);
    }

    //-----------------------------------------
    // Handle Preliminary file
    //-----------------------------------------
    let pre = preFile;
    if (pre && !(pre instanceof File) && pre?.[0]) pre = pre[0];

    if (pre instanceof File) {
      fd.append("Preliminary", pre, pre.name);
    } else if (initialData?.preliminaryUrl && isEdit) {
      fd.append("PreliminaryUrl", initialData.preliminaryUrl);
    }

    if (!values?.jobRequirements || values?.jobRequirements.trim() == "") {
      ToastifyService.showError("Please enter job description");
      return;
    }

    //-----------------------------------------
    // Salary Checks
    //-----------------------------------------
    if (values.minSalary > values.maxSalary) {
      ToastifyService.showError(
        "Minsalary should not be greater than maxSalary"
      );
      return;
    }

    //-----------------------------------------
    // Assigned positions checks
    //-----------------------------------------
    if (safeNumber(values.totalOpenPositions) > 0) {
      const assignedTotal = assigned.reduce(
        (s, p) => s + (p.assignedPositions || 0),
        0
      );

      if (assignedTotal > values.totalOpenPositions) {
        ToastifyService.showError(
          `Assigned positions (${assignedTotal}) exceed totalOpenPositions (${values.totalOpenPositions}).`
        );
        return;
      }

      if (assignedTotal < values.totalOpenPositions) {
        ToastifyService.showError(
          `Assigned positions (${assignedTotal}) should be equal to totalOpenPositions (${values.totalOpenPositions}).`
        );
        return;
      }
    }

    //-----------------------------------------
    // Now send FormData to Redux API
    //-----------------------------------------
    if (isEdit) {
      dispatch(projectActions.updateProject(fd, comId, onHide));
    } else {
      dispatch(projectActions.createProject(fd, comId, onHide));
    }
  };

  const title = isAdd
    ? "Add Project"
    : isEdit
      ? "Edit Project"
      : "View Project";

  const currencies = getAllCurrencies();

  // Map to react-select format
  const options = currencies.map((c) => ({
    value: c.currency,
    label: `${c.currency} - ${c.name} (${c.symbol})`,
  }));

  const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return "";
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.min(
      Math.floor(Math.log(bytes) / Math.log(1024)),
      sizes.length - 1
    );
    const val = bytes / Math.pow(1024, i);
    return `${val.toFixed(val >= 100 ? 0 : val >= 10 ? 1 : 2)} ${sizes[i]}`;
  };

  // select file (click-browse)
  const onJDInputChange = (e) => {
    const f = e.target.files?.[0] ?? null;
    if (!f) return;
    if (!/(\.pdf|\.docx?)$/i.test(f.name)) {
      // TODO: replace with your toast system
      ToastifyService.showError("Only PDF/DOC/DOCX allowed.");
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      ToastifyService.showError("Max file size is 2MB.");
      return;
    }
    setJDFile(f);
  };

  // drag & drop
  const onJDDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setJDDragOver(true);
  };
  const onJDDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setJDDragOver(false);
  };
  const onJDDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setJDDragOver(false);
    const f = e.dataTransfer?.files?.[0] ?? null;
    if (!f) return;
    if (!/(\.pdf|\.docx?)$/i.test(f.name)) {
      ToastifyService.showError("Only PDF/DOC/DOCX allowed.");
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      ToastifyService.showError("Max file size is 2MB.");
      return;
    }
    setJDFile(f);
  };

  const onPreInputChange = (e) => {
    const f = e.target.files?.[0] ?? null;
    if (!f) return;
    if (!/(\.pdf|\.docx?)$/i.test(f.name)) {
      // TODO: replace with your toast system
      ToastifyService.showError("Only PDF/DOC/DOCX allowed.");
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      ToastifyService.showError("Max file size is 2MB.");
      return;
    }
    setPreFile(f);
  };

  // drag & drop
  const onPreDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPreDragOver(true);
  };
  const onPreDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPreDragOver(false);
  };
  const onPreDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPreDragOver(false);
    const f = e.dataTransfer?.files?.[0] ?? null;
    if (!f) return;
    if (!/(\.pdf|\.docx?)$/i.test(f.name)) {
      ToastifyService.showError("Only PDF/DOC/DOCX allowed.");
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      ToastifyService.showError("Max file size is 2MB.");
      return;
    }
    setPreFile(f);
  };

  const disabledProps = isView ? { disabled: true, readOnly: true } : {};

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)}>
          <fieldset>
            <div className="row g-3">
              {/* Row 1: Position Name | Company */}
              <div className="col-12 col-lg-6">
                <div class="form-floating">
                  <input
                    id="name"
                    className="form-control"
                    placeholder="Position Name"
                    {...register("positionName", {
                      required: "The field is required",
                      maxLength: {
                        value: 50,
                        message: "Maximum 50 characters allowed",
                      },
                    })}
                    {...disabledProps}
                  />
                  <label for="name">Position Name</label>
                </div>
                {errors.positionName && (
                  <small className="text-danger">
                    {errors.positionName.message}
                  </small>
                )}
              </div>

              <div className="col-12 col-lg-6">
                {companyData && isCompanyDataAvailable ? (
                  <>
                    <div class="form-floating">
                      <input
                        id="companyName"
                        className="form-control"
                        value={companyData?.companyName}
                        {...register("companyName")}
                        readOnly
                      />
                      <label for="companyName">Company Name</label>
                    </div>
                    <input
                      type="hidden"
                      value={companyData?.id}
                      {...register("companyId")}
                    />
                  </>
                ) : (
                  <div class="form-floating">
                    <select
                      id="company"
                      className="form-control"
                      {...register("companyId", {
                        required: "The field is required",
                      })}
                      {...disabledProps}
                    >
                      <option value="">Select Company</option>
                      {companyAllData.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.companyName ?? r.name}
                        </option>
                      ))}
                    </select>
                    <label for="company">Company</label>
                  </div>
                )}
                {errors.companyId && (
                  <small className="text-danger">
                    {errors.companyId.message}
                  </small>
                )}
              </div>

              {/* Row 2: Total Open Positions | Job Type */}
              <div className="col-12 col-lg-6">
                <div class="form-floating">
                  <input
                    id="positions"
                    className="form-control"
                    placeholder="Total Open Positions"
                    type="number"
                    {...register("totalOpenPositions", {
                      required: "The field is required",
                    })}
                    {...disabledProps}
                  />
                  <label for="positions">Total Open Positions</label>
                </div>
                {errors.totalOpenPositions && (
                  <small className="text-danger">
                    {errors.totalOpenPositions.message}
                  </small>
                )}
              </div>
              <div className="col-12 col-lg-6">
                <div class="form-floating">
                  <select
                    id="jobType"
                    className="form-control"
                    {...register("jobType", {
                      required: "The field is required",
                    })}
                    {...disabledProps}
                  >
                    <option value="">Select Job Type</option>
                    <option value="full-time">Full-time</option>
                    <option value="part-time">Part-time</option>
                    <option value="contract">Contract</option>
                    <option value="internship">Internship</option>
                  </select>
                  <label for="jobType">Job Type</label>
                </div>
                {errors.jobType && (
                  <small className="text-danger">
                    {errors.jobType.message}
                  </small>
                )}
              </div>

              {/* Row 3: Contract Details | Frequency | Status */}
              <div className="col-12 col-lg-6">
                <div class="form-floating">
                  <select
                    id="contract"
                    className="form-control"
                    {...register("contractDetails", {
                      required: "The field is required",
                    })}
                    {...disabledProps}
                  >
                    <option value="">Select Contract Details</option>
                    <option value="onsite">Onsite</option>
                    <option value="remote">Remote</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                  <label for="contract">Contract Details</label>
                </div>
                {errors.contractDetails && (
                  <small className="text-danger">
                    {errors.contractDetails.message}
                  </small>
                )}
              </div>
              <div className="col-12 col-lg-6">
                <div class="form-floating">
                  <select
                    id="frequency"
                    className="form-control"
                    {...register("frequency", {
                      required: "The field is required",
                    })}
                    {...disabledProps}
                  >
                    <option value="">Select Frequency</option>
                    <option value="hourly">Hourly</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                  <label for="frequency">Frequency</label>
                </div>
                {errors.frequency && (
                  <small className="text-danger">
                    {errors.frequency.message}
                  </small>
                )}
              </div>
              <div className="col-12 col-lg-6">
                <div class="form-floating">
                  <select
                    id="projectStatus"
                    className="form-control w-100"
                    {...register("status", {
                      required: "The field is required",
                    })}
                    {...disabledProps}
                  >
                    <option value="">Select Status</option>

                    <option value={statusConstants.OPEN}>
                      {statusConstants.OPEN_LABEL}
                    </option>
                    <option value={statusConstants.PENDING}>
                      {statusConstants.PENDING_LABEL}
                    </option>
                    <option value={statusConstants.HOLD}>
                      {statusConstants.HOLD_LABEL}
                    </option>
                    <option value={statusConstants.INPROGRESS}>
                      {statusConstants.INPROGRESS_LABEL}
                    </option>
                    <option value={statusConstants.COMPLETED}>
                      {statusConstants.COMPLETED_LABEL}
                    </option>
                    <option value={statusConstants.CLOSED}>
                      {statusConstants.CLOSED_LABEL}
                    </option>
                  </select>
                  <label for="projectStatus">Status</label>
                </div>
                {errors.status && (
                  <small className="text-danger">{errors.status.message}</small>
                )}
              </div>

              {/* Row 4: Location */}
              <div className="col-12 col-lg-6">
                <div class="form-floating">
                  <input
                    id="location"
                    className="form-control"
                    placeholder="Location"
                    {...register("location", {
                      required: "The field is required",
                    })}
                    {...disabledProps}
                  />
                  <label for="location">Location</label>
                </div>
                {errors.location && (
                  <small className="text-danger">
                    {errors.location.message}
                  </small>
                )}
              </div>

              {/* Row 5: Min | Max Salary */}
              <div className="col-12 col-lg-6">
                <div class="form-floating">
                  <input
                    id="nationality"
                    className="form-control"
                    placeholder="Add Nationality"
                    {...register("nationality", {
                      maxLength: {
                        value: 50,
                        message: "Maximum 50 characters allowed",
                      },
                    })}
                    {...disabledProps}
                  />
                  <label for="nationality">Nationality</label>
                </div>
              </div>

              <div className="col-12 col-lg-6">
                <div class="form-floating">
                  <input
                    id="minimumExperienceRquired"
                    className="form-control"
                    placeholder="Add Minimum Experience Rquired"
                    type="number"
                    {...register("minimumExperienceRquired")}
                    {...disabledProps}
                  />
                  <label for="minimumExperienceRquired">
                    Minimum Experience In Years
                  </label>
                </div>
              </div>
              <div className="col-12 col-lg-6">
                <div className="form-floating">
                  <select
                    id="gender"
                    className="form-control"
                    {...register("gender")}
                    {...disabledProps}
                  >
                    <option value="">Gender</option>
                    {genderOptions.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="gender">Gender</label>
                </div>
              </div>

              <div className="col-12 col-lg-6">
                <div className="form-floating">
                  <select
                    id="range"
                    className="form-control"
                    {...register("salaryRange")}
                    {...disabledProps}
                  >
                    <option value="">Salary Range</option>
                    {salaryRangeOptions.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="range">Salary Range</label>
                </div>
              </div>

              <div className="col-12 col-lg-12">
                <Controller
                  name="currency"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <div className="form-floating">
                      <Select
                        {...field}
                        options={options}
                        isSearchable
                        placeholder="Currency"
                        isDisabled={isView}
                        classNamePrefix="react-select"
                        onChange={(selectedOption) => {
                          field.onChange(selectedOption?.value);
                        }}
                        value={
                          options.find((opt) => opt.value === field.value) ||
                          null
                        }
                      />

                      {/* Display error message */}
                      {error && (
                        <p className="text-danger" style={{ marginTop: "5px" }}>
                          {error.message}
                        </p>
                      )}
                    </div>
                  )}
                />
              </div>
              {/* Row 6: Assigned Open Positions (Add More) */}
              <div className="col-12">
                <div className="d-flex align-items-center justify-content-between">
                  <span className="form-text">Assigned Open Positions</span>
                  {!isView && (
                    <button
                      type="button"
                      className="btn btn-sm btn-primary btn-footer"
                      onClick={() =>
                        append({ userId: "", assignedPositions: 1 })
                      }
                    >
                      Add More
                    </button>
                  )}
                </div>

                <div className="mt-2">
                  {fields.map((f, idx) => (
                    <div className="row g-2 align-items-center mb-2" key={f.id}>
                      {/* Recruiter (filtered per row) */}
                      <div className="col-12 col-lg-6">
                        <div class="form-floating">
                          <select
                            id="projectPosition"
                            className="form-control"
                            {...register(`projectPositions.${idx}.userId`)}
                            {...disabledProps}
                          >
                            <option value="">Select Recruiter</option>
                            {filteredRecruitersFor(idx).map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.name}
                              </option>
                            ))}
                          </select>
                          <label for="projectPosition">Recruiter</label>
                        </div>
                      </div>

                      {/* assignedPositions */}
                      <div className="col-12 col-lg-5">
                        <div class="form-floating">
                          <input
                            id="assignedPosition"
                            type="number"
                            min="1"
                            className="form-control"
                            placeholder="assignedPositions"
                            {...register(
                              `projectPositions.${idx}.assignedPositions`,
                              {
                                valueAsNumber: true,
                              }
                            )}
                            {...disabledProps}
                          />
                          <label for="assignedPosition">
                            Assigned Positions
                          </label>
                        </div>
                      </div>

                      {!isView && (
                        <div className="col-1 d-grid">
                          <button
                            className="btn text-danger"
                            type="button"
                            onClick={() => remove(idx)}
                            title="Remove"
                          >
                            <i className="bi bi-x-lg" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  {fields.length === 0 && (
                    <div className="text-muted small">
                      No recruiter assignments yet.
                    </div>
                  )}
                </div>
              </div>

              {/* Row 7: Detail Job Requirements */}
              <div className="col-12">
                <div class="form-floating">
                  {/* <textarea
                    id="jd"
                    className="form-control"
                    rows={6}
                    placeholder="Detail Job Requirements"
                    {...register("jobRequirements", {
                      required: "The field is required",
                    })}
                    {...disabledProps}
                  /> */}
                  <JoditEditorComponent
                    defaultValue={watch("jobRequirements")}
                    onChange={(content) => setValue("jobRequirements", content)}
                    height={250}
                    required={true}
                    {...disabledProps}
                  />
                  {/* <label for="jd">Job Requirements</label> */}
                </div>
                {errors.jobRequirements && (
                  <small className="text-danger">
                    {errors.jobRequirements.message}
                  </small>
                )}
              </div>
              <div className="row">
                <div className="col-12 col-lg-6 mt-5">
                  <label className="form-label d-block mb-2">JD File</label>

                  {/* Hidden input for click-to-browse */}
                  <input
                    ref={jdInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    className="d-none"
                    disabled={isView}
                    onChange={onJDInputChange}
                    {...disabledProps}
                  />

                  {/* Dropzone */}
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => !isView && jdInputRef.current?.click()}
                    onKeyDown={(e) => {
                      if ((e.key === "Enter" || e.key === " ") && !isView) {
                        jdInputRef.current?.click();
                      }
                    }}
                    onDragOver={onJDDragOver}
                    onDragLeave={onJDDragLeave}
                    onDrop={onJDDrop}
                    className={isView ? "d-none" : "d-block p-3 text-center"}
                    style={{
                      border: "2px dashed",
                      borderColor: jdDragOver ? "#2e6bff" : "#ced4da",
                      borderRadius: 10,
                      background: jdDragOver
                        ? "rgba(46,107,255,0.06)"
                        : "#f8f9fb",
                      transition: "all .15s ease",
                      cursor: isView ? "not-allowed" : "pointer",
                      userSelect: "none",
                    }}
                  >
                    <div className="d-flex flex-column align-items-center">
                      <i
                        className={`bi ${jdFile
                          ? "bi-file-earmark-check"
                          : "bi-file-earmark-arrow-up"
                          } mb-2`}
                        style={{
                          fontSize: 28,
                          color: jdDragOver ? "#2e6bff" : "#6c757d",
                        }}
                      />
                      <div className="fw-semibold" style={{ fontSize: 14 }}>
                        {jdFile ? "File selected" : "Drag & drop JD here"}
                      </div>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        or{" "}
                        <span className="text-primary text-decoration-underline">
                          browse
                        </span>{" "}
                        to choose a file
                      </div>
                      <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                        Accepted: PDF, DOC, DOCX • Max ~10MB
                      </div>
                      {jdFile && (
                        <div className="mt-2 small">
                          {jdFile.name} • {formatBytes(jdFile.size)}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Status row */}
                  <div className="d-flex align-items-center justify-content-between mt-2">
                    <small className="text-muted">
                      {initialData?.jdUrl && isEdit
                        ? "Edit or view uploaded jd"
                        : initialData?.jdUrl && isView
                          ? "View uploaded jd"
                          : jdFile
                            ? "JD ready to upload"
                            : "No JD exists"}
                    </small>

                    {jdFile && !isView ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          setJDFile(null);
                          if (jdInputRef.current) jdInputRef.current.value = "";
                        }}
                      >
                        Remove
                      </button>
                    ) : initialData?.jdUrl && (isEdit || isView) ? (
                      <a
                        href={
                          process.env.REACT_APP_STORAGE_URL + initialData?.jdUrl
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="fs-5 btn btn-success"
                      >
                        <i className="bi bi-eye text-light"></i>
                      </a>
                    ) : (
                      ""
                    )}
                  </div>
                </div>

                <div className="col-12 col-lg-6 mt-5">
                  <label className="form-label d-block mb-2">
                    Preliminary File
                  </label>

                  {/* Hidden input for click-to-browse */}
                  <input
                    ref={preInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    className="d-none"
                    disabled={isView}
                    onChange={onPreInputChange}
                    {...disabledProps}
                  />

                  {/* Dropzone */}
                  <div
                    role="button"
                    className={isView ? "d-none" : "d-block p-3 text-center"}
                    tabIndex={0}
                    onClick={() => !isView && preInputRef.current?.click()}
                    onKeyDown={(e) => {
                      if ((e.key === "Enter" || e.key === " ") && !isView) {
                        preInputRef.current?.click();
                      }
                    }}
                    onDragOver={onPreDragOver}
                    onDragLeave={onPreDragLeave}
                    onDrop={onPreDrop}
                    style={{
                      border: "2px dashed",
                      borderColor: preDragOver ? "#2e6bff" : "#ced4da",
                      borderRadius: 10,
                      background: preDragOver
                        ? "rgba(46,107,255,0.06)"
                        : "#f8f9fb",
                      transition: "all .15s ease",
                      cursor: isView ? "not-allowed" : "pointer",
                      userSelect: "none",
                    }}
                  >
                    <div className="d-flex flex-column align-items-center">
                      <i
                        className={`bi ${preFile
                          ? "bi-file-earmark-check"
                          : "bi-file-earmark-arrow-up"
                          } mb-2`}
                        style={{
                          fontSize: 28,
                          color: preDragOver ? "#2e6bff" : "#6c757d",
                        }}
                      />
                      <div className="fw-semibold" style={{ fontSize: 14 }}>
                        {preFile
                          ? "File selected"
                          : "Drag & drop Preliminary here"}
                      </div>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        or{" "}
                        <span className="text-primary text-decoration-underline">
                          browse
                        </span>{" "}
                        to choose a file
                      </div>
                      <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                        Accepted: PDF, DOC, DOCX • Max ~10MB
                      </div>
                      {preFile && (
                        <div className="mt-2 small">
                          {preFile.name} • {formatBytes(preFile.size)}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Status row */}
                  <div className="d-flex align-items-center justify-content-between mt-2">
                    <small className="text-muted">
                      {initialData?.preliminaryUrl && isEdit
                        ? "Edit or view uploaded preliminary file"
                        : initialData?.preliminaryUrl && isView
                          ? "View uploaded preliminary file"
                          : preFile
                            ? "Preliminary ready to upload"
                            : "No Preliminary exists"}
                    </small>

                    {preFile && !isView ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreFile(null);
                          if (preInputRef.current)
                            preInputRef.current.value = "";
                        }}
                      >
                        Remove
                      </button>
                    ) : initialData?.preliminaryUrl && (isEdit || isView) ? (
                      <a
                        href={
                          process.env.REACT_APP_STORAGE_URL +
                          initialData?.preliminaryUrl
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="fs-5 btn btn-success"
                      >
                        <i className="bi bi-eye text-light"></i>
                      </a>
                    ) : (
                      ""
                    )}
                  </div>
                </div>
              </div>
            </div>
          </fieldset>

          <div className="d-flex justify-content-end gap-2 mt-4 footer-modal">
            <Button
              variant="outline-secondary"
              onClick={onHide}
              className="btn-footer"
            >
              {isView ? "Close" : "Cancel"}
            </Button>
            {!isView && (
              <Button type="submit" variant="success" className="btn-footer">
                {isEdit ? "Save Changes" : "Create Project"}
              </Button>
            )}
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}
